// TaskDescriptionField.jsx - Always-editable description field with enhanced UI
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  ClickAwayListener,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

// Import mention components
import InlineMentionInput from '../../ui/FormFields/InlineMentionInput';
import MentionText from '../../ui/MentionText';

/**
 * TaskDescriptionField - Always-editable description field with enhanced UI
 * Features:
 * - Always editable for better UX (no click required)
 * - Consistent container background/border like checklist and subtasks
 * - Compact header with icon
 * - Auto-save functionality
 * - @mention support
 * - Seamless editing experience
 */
const TaskDescriptionField = ({
  value,
  onChange,
  onMentionedUsersChange,
  placeholder = 'Add a description...',
  multiline = true,
  rows = 3,
  disabled = false,
  sx = {},
}) => {
  const theme = useTheme();
  const [localValue, setLocalValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  
  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  const isEmpty = !value;
  
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.2s ease',
        ...sx,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1.5,
        pb: 1,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ 
            fontSize: 16, 
            color: theme.palette.text.secondary 
          }} />
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '0.7rem'
            }}
          >
            Description
          </Typography>
        </Box>
        
        {/* Visual indicator that field is editable */}
        {!disabled && (
          <Fade in={isHovered || isFocused}>
            <EditIcon 
              sx={{ 
                fontSize: 14, 
                color: isFocused ? theme.palette.primary.main : theme.palette.text.disabled,
                opacity: isFocused ? 1 : 0.5,
                transition: 'all 0.2s ease'
              }} 
            />
          </Fade>
        )}
      </Box>
      
      {/* Always Editable Content Section */}
      <Box 
        sx={{
          p: 1.5,
          borderRadius: 0.5,
          bgcolor: isFocused 
            ? alpha(theme.palette.primary.main, 0.02)
            : isHovered 
              ? alpha(theme.palette.action.hover, 0.02)
              : 'transparent',
          border: `1px solid ${
            isFocused 
              ? alpha(theme.palette.primary.main, 0.3)
              : isHovered
                ? alpha(theme.palette.divider, 0.1)
                : 'transparent'
          }`,
          transition: 'all 0.2s ease',
        }}
      >
        <InlineMentionInput
          ref={inputRef}
          value={localValue}
          onChange={(newValue) => {
            setLocalValue(newValue);
            // Immediately notify parent to enable auto-save
            if (onChange) {
              onChange(newValue);
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            // Save on blur
            if (onMentionedUsersChange && mentionedUsers.length > 0) {
              onMentionedUsersChange(mentionedUsers);
            }
          }}
          onMentionAdd={(user) => {
            setMentionedUsers(prev => [...prev.filter(u => u._id !== user._id), user]);
          }}
          onMentionRemove={(userId) => {
            setMentionedUsers(prev => prev.filter(u => u._id !== userId));
          }}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{
            style: { 
              minHeight: '100px',
              maxHeight: isExpanded ? 'none' : '200px',
              overflow: 'auto',
              padding: '8px',
              fontSize: '14px',
              lineHeight: '1.6',
              cursor: disabled ? 'not-allowed' : 'text',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
            },
            'data-placeholder': placeholder,
          }}
        />
        
        {/* Expand/Collapse for long content */}
        {localValue && localValue.length > 200 && (
          <Button
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              mt: 1,
              textTransform: 'none',
              fontSize: '0.75rem',
              p: '2px 6px',
              minWidth: 'auto',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Box>
      
      {/* Helper text when focused */}
      {isFocused && (
        <Fade in>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              mt: 1,
              color: theme.palette.text.secondary,
              fontSize: '0.7rem'
            }}
          >
            Press @ to mention • Auto-saves as you type
          </Typography>
        </Fade>
      )}
    </Box>
  );
};

export default TaskDescriptionField;